import React, { useState } from 'react';
import { formatSignedCarry } from '../utils/salaryOverview';
import { buildSalaryCycleDisplay } from '../utils/salaryCycleDisplay';

const Row = ({ label, value, formatCurrency, tone = 'text-gray-800', bold = false }) => (
  <div className="grid grid-cols-[minmax(0,1fr)_9rem] items-center gap-3 py-1.5 text-xs sm:text-sm">
    <span className="text-gray-600">{label}</span>
    <span className={`text-right whitespace-nowrap ${bold ? 'font-bold' : 'font-semibold'} ${tone}`}>
      {typeof value === 'string' ? value : formatCurrency(value || 0)}
    </span>
  </div>
);

const recordDate = (record = {}) => record.advance_date || record.date || '';

export const currentCycleDeductions = (records = [], cycleStart, cycleEnd, asOfDate) => (
  (Array.isArray(records) ? records : []).filter((record) => {
    const date = recordDate(record);
    if (!date) return false;
    if (cycleStart && date < cycleStart) return false;
    if (cycleEnd && date > cycleEnd) return false;
    if (asOfDate && date > asOfDate) return false;
    return true;
  })
);

const ExpandableLedgerRow = ({
  label,
  value,
  records = [],
  formatCurrency,
  formatDate,
  tone = 'text-emerald-700',
  detailLabel = 'Note',
  amountLabel = 'Amount',
  emptyMessage = 'No itemized records are available for this cycle.',
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        className="grid w-full grid-cols-[minmax(0,1fr)_9rem] items-center gap-3 py-1.5 text-left text-xs sm:text-sm"
      >
        <span className="flex items-center gap-2 text-gray-600">
          <span className={`text-[9px] transition-transform ${expanded ? 'rotate-90' : ''}`} aria-hidden="true">&#9654;</span>
          {label}
        </span>
        <span className={`text-right font-semibold ${tone}`}>{formatCurrency(value || 0)}</span>
      </button>
      {expanded && (
        <div className="mb-2 overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <div className="min-w-[32rem]">
            <div className="grid grid-cols-[7rem_minmax(14rem,1fr)_8rem] gap-3 bg-gray-50 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-gray-600">
              <span>Date</span>
              <span>{detailLabel}</span>
              <span className="text-right">{amountLabel}</span>
            </div>
            {records.length > 0 ? records.map((record, index) => (
              <div
                key={record.id || `${recordDate(record)}-${index}`}
                className={`grid grid-cols-[7rem_minmax(14rem,1fr)_8rem] gap-3 border-t px-3 py-2 text-xs ${record.classificationMismatch ? 'border-red-100 bg-red-50' : 'border-gray-100'}`}
              >
                <span className="text-gray-500">{formatDate(recordDate(record))}</span>
                <span className={`min-w-0 whitespace-normal break-words ${record.classificationMismatch ? 'font-semibold text-red-700' : 'text-gray-700'}`}>
                  <span>{record.reason || record.violation_type_display || record.penalty || detailLabel}</span>
                  {record.classificationWarning ? (
                    <span className="mt-0.5 block text-[10px] font-bold uppercase tracking-wide text-red-700">
                      {record.classificationWarning}
                    </span>
                  ) : null}
                </span>
                <span className={`text-right font-semibold ${record.classificationMismatch ? 'text-red-700' : tone}`}>
                  {formatCurrency(record.amount ?? record.penalty_amount ?? 0)}
                </span>
              </div>
            )) : (
              <p className="border-t border-gray-100 px-3 py-3 text-xs text-gray-500">
                {emptyMessage}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Subsection = ({ title, children, tone = 'bg-gray-50 border-gray-100' }) => (
  <div className={`rounded-lg border px-3 py-2 ${tone}`}>
    <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-500">{title}</p>
    <div className="divide-y divide-gray-100/80">{children}</div>
  </div>
);

const Cycle = ({ title, range, children, accent = 'border-gray-200' }) => (
  <section className={`rounded-xl border ${accent} bg-white p-3 shadow-sm`}>
    <div className="mb-2 flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 pb-2">
      <h4 className="text-xs font-bold uppercase tracking-wide text-gray-700">{title}</h4>
      <span className="text-[10px] text-gray-400">{range}</span>
    </div>
    <div className="space-y-2">{children}</div>
  </section>
);

const SalaryCycleSummary = ({
  summary = {}, breakdown = {}, deductionRecords = [], asOfDate, formatCurrency, formatDate,
}) => {
  const display = buildSalaryCycleDisplay(summary);
  const isDaily = (breakdown.wageType || summary.wageType) === 'daily_wages';
  const previousRange = summary.previousCycleStart && summary.previousCycleEnd
    ? `${formatDate(summary.previousCycleStart)} to ${formatDate(summary.previousCycleEnd)}`
    : '-';
  const currentRange = summary.cycleStart && summary.cycleEnd
    ? `${formatDate(summary.cycleStart)} to ${formatDate(summary.cycleEnd)}`
    : '-';
  const carry = Number(summary.openingSignedCarryForward ?? summary.signedCarryForward ?? 0);
  const previousCarry = Number.isFinite(Number(summary.previousCycleTrace?.total))
    ? -Number(summary.previousCycleTrace.total)
    : carry;
  const runningBalance = Number(summary.runningSalaryBalance ?? summary.unpaidAsOfToday ?? 0);
  const salaryAfterDeductions = Number(
    summary.currentCycleSalaryAfterDeductions
      ?? (Number(breakdown.grossEarned || 0) - display.current.deductions)
  );
  const totalNonSalaryPayments = Number(
    summary.totalNonSalaryPayments ?? display.current.nonSalaryReceived
  );
  const totalReceivablePayments = Number(
    summary.totalReceivablePayments ?? (runningBalance + totalNonSalaryPayments)
  );
  const deductionDetails = currentCycleDeductions(
    deductionRecords,
    summary.cycleStart,
    summary.cycleEnd,
    asOfDate,
  );
  const previousTrace = summary.previousCycleTrace || {};
  const currentPaymentTrace = summary.currentCyclePaymentTrace || {};
  const isCurrentCycle = summary.cycleDisplayState !== 'selected';
  const cycleTitle = isCurrentCycle ? 'Current Month Salary Cycle' : 'Selected Month Salary Cycle';
  const earningsLabel = isCurrentCycle
    ? `c. Accrued salary as of ${formatDate(asOfDate)}`
    : `c. Salary earned through ${formatDate(summary.cycleEnd)}`;
  const balanceLabel = isCurrentCycle
    ? `i. Accrued salary balance as of ${formatDate(asOfDate)} (e - f - g - h)`
    : `i. Outstanding for selected cycle as of ${formatDate(asOfDate)} (e - f - g - h)`;

  return (
    <div className="space-y-3 p-3">
      <Cycle title="Prev. Salary Cycle" range={previousRange} accent="border-slate-200">
        <Subsection title="Previous cycle account">
          <Row label="a. Salary / wages earned" value={display.previous.earned} formatCurrency={formatCurrency} tone="text-emerald-700" />
          <ExpandableLedgerRow label="b. Advances paid" value={display.previous.advances} records={previousTrace.advances} formatCurrency={formatCurrency} formatDate={formatDate} />
          <ExpandableLedgerRow label="c. Salary paid" value={display.previous.salaryPaid} records={previousTrace.salaryPayments} formatCurrency={formatCurrency} formatDate={formatDate} />
          <ExpandableLedgerRow label="d. Deductions" value={display.previous.deductions} records={previousTrace.deductions} formatCurrency={formatCurrency} formatDate={formatDate} tone="text-rose-700" detailLabel="Penalty" amountLabel="Deduction" />
          <Row label="e. Over / underpayment carried forward" value={formatSignedCarry(previousCarry, formatCurrency)} formatCurrency={formatCurrency} tone={previousCarry >= 0 ? 'text-emerald-700' : 'text-amber-700'} bold />
        </Subsection>
      </Cycle>

      <Cycle title={cycleTitle} range={currentRange} accent="border-primary-200">
        <Subsection title="Leaves & off-days">
          <Row label="Holidays" value={`${breakdown.holidays ?? 0} day(s)`} formatCurrency={formatCurrency} />
          <Row label="Paid leaves" value={`${breakdown.paidLeaves ?? 0} day(s)`} formatCurrency={formatCurrency} />
          <Row label="Unpaid leaves" value={`${breakdown.unpaidLeaves ?? 0} day(s)`} formatCurrency={formatCurrency} />
          <Row label="Waived absentee" value={`${breakdown.waivedAbsentee ?? 0} day(s)`} formatCurrency={formatCurrency} />
          <Row label="Deducted absentee" value={`${breakdown.deductedAbsentee ?? 0} day(s)`} formatCurrency={formatCurrency} />
        </Subsection>
        <Subsection title={isDaily ? (isCurrentCycle ? 'Current wages' : 'Selected-cycle wages') : (isCurrentCycle ? 'Current salary' : 'Selected-cycle salary')}>
          {isDaily ? (
            <>
              <Row label="a. Daily wage rate" value={breakdown.dailyRate} formatCurrency={formatCurrency} />
              <Row label="b. Days present" value={`${breakdown.daysPresent ?? 0} day(s)`} formatCurrency={formatCurrency} />
            </>
          ) : (
            <>
              <Row label="a. Monthly salary" value={Number(breakdown.dailyRate || 0) * 30} formatCurrency={formatCurrency} />
              <Row label="b. Attendance" value={`${breakdown.daysPresent ?? 0} present; ${breakdown.paidLeaves ?? 0} paid leave`} formatCurrency={formatCurrency} />
            </>
          )}
          <Row label={earningsLabel} value={breakdown.grossEarned} formatCurrency={formatCurrency} tone="text-emerald-700" bold />
          <ExpandableLedgerRow
            label="d. Deductions in current cycle"
            value={display.current.deductions}
            records={deductionDetails}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
            tone="text-rose-700"
            detailLabel="Penalty"
            amountLabel="Deduction"
          />
          <Row label="e. Salary after deductions (c - d)" value={salaryAfterDeductions} formatCurrency={formatCurrency} tone="text-primary-700" bold />
        </Subsection>
      </Cycle>

      <Cycle title="Payments" range={currentRange} accent="border-emerald-200">
        <Subsection title="Salary payments & carry-forward">
          <ExpandableLedgerRow label="f. Advances paid in current cycle" value={display.current.advances} records={currentPaymentTrace.advances} formatCurrency={formatCurrency} formatDate={formatDate} />
          <ExpandableLedgerRow label="g. Salary paid in current cycle" value={display.current.salaryPaid} records={currentPaymentTrace.salaryPayments} formatCurrency={formatCurrency} formatDate={formatDate} />
          <Row label="h. Over / underpayment brought forward" value={formatSignedCarry(carry, formatCurrency)} formatCurrency={formatCurrency} tone={carry >= 0 ? 'text-emerald-700' : 'text-amber-700'} />
        </Subsection>
        <Subsection title="Salary balance" tone="bg-primary-50/50 border-primary-100">
          <Row label={balanceLabel} value={runningBalance} formatCurrency={formatCurrency} tone="text-primary-700" bold />
        </Subsection>
        <Subsection title="Non-Salary Payments" tone="bg-emerald-50/50 border-emerald-100">
          <ExpandableLedgerRow label="Bonus received" value={display.current.bonus} records={currentPaymentTrace.bonuses} formatCurrency={formatCurrency} formatDate={formatDate} />
          <ExpandableLedgerRow label="Gift received" value={display.current.gift} records={currentPaymentTrace.gifts} formatCurrency={formatCurrency} formatDate={formatDate} />
          <Row label="j. Total" value={totalNonSalaryPayments} formatCurrency={formatCurrency} tone="text-emerald-800" bold />
        </Subsection>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
          <Row label="Total Receivable Payments (i + j)" value={totalReceivablePayments} formatCurrency={formatCurrency} tone="text-emerald-800" bold />
        </div>
      </Cycle>
    </div>
  );
};

export default SalaryCycleSummary;
